<template>
  <v-container v-if="sessionLoaded">
    <v-row v-if="!agent && !identification" justify="center">
      <v-card
        max-width="600"
        :class="$vuetify.breakpoint.smAndDown ? 'px-4 elevation-0 mt-4' : 'mt-8'"
      >
        <v-card-title :class="{ 'px-6 pt-5': $vuetify.breakpoint.mdAndUp }" class="text-h5">
          Sähköinen sopimusmenettely
        </v-card-title>
        <v-card-text>
          <v-alert type="info" text>
            Sinun täytyy tunnistautua sähköisesti pankkitunnuksillasi,
            jotta voit allekirjoittaa asiakirjoja.
          </v-alert>
          <v-checkbox
            v-model="acceptGdpr"
            hide-details
            color="primary"
            class="mt-3"
            label="Hyväksyn henkilötietojeni sähköisen käsittelyn"
          />
          <p class="text-caption mt-2">
            Sähköinen sopimusmenettely edellyttää henkilötietojen automaattista käsittelyä
            ja tietojen tallennusta asiakasrekisteriimme.
            <a @click="showGdpr = true">
              EU:n tietosuoja-asetuksen (GDPR) mukainen tietosuojaseloste.
            </a>
          </p>
          <v-dialog v-model="showGdpr" max-width="600">
            <gdpr/>
          </v-dialog>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            :disabled="!acceptGdpr"
            text
            color="primary"
            @click="netsLink()"
          >
            Tunnistaudu
            <v-icon>
              chevron_right
            </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-row>
    <v-row v-else>
      <v-col :md="agent || fileProcess.personRole === 'observer' ? 8 : 12" cols="12">
        <v-card class="elevation-0 transparent" :class="{ 'mx-4': $vuetify.breakpoint.mdAndUp }">
          <v-card-text class="pb-0" :class="{ 'px-6 pt-5': $vuetify.breakpoint.mdAndUp }">
            <div v-if="fileProcess.property_name">
              {{ fileProcess.property_name }} | <span class="primary--text">
                {{ fileProcess.name }}
              </span>
            </div>
            <p class="text-h5 text--primary mb-1">
              Sähköinen sopimusmenettely
            </p>
          </v-card-text>
        </v-card>
        <v-card
          v-if="agent"
          :class="{
            'ma-6 elevation-15 pb-0': $vuetify.breakpoint.mdAndUp,
            'elevation-0 mt-4': $vuetify.breakpoint.smAndDown,
          }"
          :outlined="$vuetify.breakpoint.smAndDown"
        >
          <template>
            <v-list
              class="mb-2"
              two-line
              dense
              subheader
            >
              <v-subheader :class="$vuetify.breakpoint.mdAndUp ? 'px-6' : 'px-4'">
                1. Henkilöt
              </v-subheader>
              <v-divider/>
              <template
                v-for="(item, s) in people"
              >
                <v-menu
                  :key="`${s}-A`"
                  offset-y
                >
                  <template v-slot:activator="{ on }">
                    <v-list-item
                      :class="{ 'px-6': $vuetify.breakpoint.mdAndUp }"
                      class="grey lighten-5"
                    >
                      <v-list-item-avatar
                        class="mr-4"
                        :size="$vuetify.breakpoint.mdAndUp ? 40 : 30"
                      >
                        <v-icon
                          :class="item.person_role === 'signer' ? 'orange' : 'blue'"
                          class="white--text"
                          :size="$vuetify.breakpoint.mdAndUp ? 24 : 20"
                        >
                          {{ item.person_role === 'signer' ? 'create' : 'remove_red_eye' }}
                        </v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="primary--text">
                          {{ item.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ item.email }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle class="text-caption">
                          {{ item.ssn }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-avatar class="my-0">
                        <v-btn
                          v-if="!usersThatHaveSigned.includes(item.id)"
                          icon
                          v-on="on"
                        >
                          <v-icon>
                            more_vert
                          </v-icon>
                        </v-btn>
                      </v-list-item-avatar>
                    </v-list-item>
                  </template>
                  <v-list>
                    <!-- <v-list-item @click="editOfferer(index)">
                      <v-list-item-title>Muokkaa</v-list-item-title>
                    </v-list-item> -->
                    <v-list-item @click="removePersonFromSigningProcess(item)">
                      <v-list-item-title>Poista</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-divider :key="`${s}-B`"/>
              </template>
            </v-list>
            <v-dialog
              v-model="dialog"
              :fullscreen="$vuetify.breakpoint.smAndDown"
              width="550"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  :class="$vuetify.breakpoint.mdAndUp ? 'mx-6' : 'ml-3'"
                  color="primary"
                  class="mb-5"
                  large
                  v-on="on"
                  @click="resetForm()"
                >
                  Lisää
                  <v-icon right dark>
                    person_add
                  </v-icon>
                </v-btn>
              </template>
              <v-card>
                <v-card-title class="text-h5">
                  Lisää henkilö
                </v-card-title>
                <v-card-text class="pt-4 pb-0">
                  <v-autocomplete
                    v-model="selectedPersonId"
                    :loading="loading"
                    :items="customers"
                    :search-input.sync="search"
                    item-text="name"
                    item-value="id"
                    autocomplete="off"
                    label="Hae"
                    prepend-inner-icon="search"
                    outlined
                  >
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-title v-if="!search || (search && search.length < 3)">
                          Syötä vähintään
                          <strong>3</strong>
                          kirjainta.
                        </v-list-item-title>
                        <v-list-item-title v-else>
                          Ei hakutuloksia
                        </v-list-item-title>
                      </v-list-item>
                    </template>
                    <template v-slot:selection="data">
                      <v-chip
                        v-if="selectedPersonId"
                        v-bind="data.attrs"
                        :input-value="data.selected"
                        @click="data.select"
                      >
                        <v-avatar left>
                          <v-icon>person</v-icon>
                        </v-avatar>
                        {{ data.item.name }}
                        {{ data.item.ssn }}
                      </v-chip>
                    </template>
                    <template v-slot:item="data">
                      <template>
                        <v-list-item-avatar>
                          <v-icon>person</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title v-text="data.item.name"/>
                          <v-list-item-subtitle v-text="data.item.ssn"/>
                        </v-list-item-content>
                      </template>
                    </template>
                  </v-autocomplete>
                  <file-signer-form
                    :people="people"
                    :selected-person-id="selectedPersonId"
                    :new-person="newPerson"
                  />
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                  <v-spacer/>
                  <v-btn
                    color="primary"
                    depressed
                    :loading="addingPerson"
                    :disabled="!validateEmail(newPerson.email) || !validateSsn(newPerson.ssn)"
                    @click="addPersonToSigningProcess()"
                  >
                    Lisää henkilö
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </template>
        </v-card>
        <v-card
          :class="{
            'ma-6 elevation-15 pb-1': $vuetify.breakpoint.mdAndUp,
            'elevation-0 my-5': $vuetify.breakpoint.smAndDown,
          }"
          :outlined="$vuetify.breakpoint.smAndDown"
        >
          <v-list
            two-line
            subheader
            dense
            class="pb-0 pt-2"
          >
            <v-subheader
              v-if="signedFiles && signedFiles.length > 0"
              :class="{ 'px-6': $vuetify.breakpoint.mdAndUp }"
            >
              Allekirjoitetut asiakirjat
            </v-subheader>
            <template v-for="(item, index) in signedFiles">
              <v-divider v-if="index < 1" :key="`${index}-X`"/>
              <v-list-item
                :key="`${index}-A`"
                :class="{ 'px-6': $vuetify.breakpoint.mdAndUp }"
                class="green lighten-5"
                @click="item.selected = !item.selected"
              >
                <v-list-item-avatar class="mr-4">
                  <v-icon class="green--text">
                    how_to_reg
                  </v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title v-text="item.name"/>
                  <v-list-item-subtitle>
                    {{ humanFileSize(item.size, false) }} - allekirjoitettu
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-avatar>
                  <v-btn icon @click.stop="download(item.name, item.id)">
                    <v-icon>
                      save_alt
                    </v-icon>
                  </v-btn>
                </v-list-item-avatar>
              </v-list-item>
              <v-divider :key="`${index}-zz`"/>
            </template>

            <v-subheader
              v-if="(fileList && fileList.length > 0) || agent"
              :class="$vuetify.breakpoint.mdAndUp ? 'px-6' : 'px-4'"
            >
              2. Allekirjoitettavat asiakirjat
            </v-subheader>
            <v-alert
              v-else-if="!agent && signedFiles.length"
              type="info"
              class="mx-4 mt-4"
              text
            >
              Olet allekirjoittanut kaikki asiakirjat
            </v-alert>
            <template v-else-if="!agent && !loadingFileList">
              <v-alert
                type="warning"
                class="mx-4 mt-4"
                text
              >
                Tunnistautujan henkilötunnusta vastaavalle henkilötunnukselle ei ole osoitettu
                sähköistä allekirjoitusta vaativia asiakirjoja.
              </v-alert>
              <v-alert
                type="info"
                text
                class="mx-4"
              >
                Sopimusmenettelyyn kirjattu henkilötunnus voi olla väärä tai menettelyn
                asiakirjoihin ei lisätty allekirjoituspyyntöä henkilötunnuksellesi.
                Epäselvissä tilanteissa olethan yhteydessä välittäjääsi.
              </v-alert>
            </template>
            <v-divider v-if="!fileList.length && agent"/>
            <v-progress-linear
              v-if="loadingFileList"
              color="primary"
              indeterminate
            />
            <template v-for="(item, index) in fileList">
              <v-divider v-if="index < 1" :key="`${index}-XS`"/>
              <v-list-item
                :key="index"
                class="lighten-5 px-2"
                :class="[{
                  'px-6': $vuetify.breakpoint.mdAndUp,
                }, {
                  'green': !item.persons
                    .filter(p => p.person_role === 'signer')
                    .some(p => !p.signature) && item.persons.length,
                }, {
                  'grey' : item.persons
                    .filter(p => p.person_role === 'signer')
                    .some(p => !p.signature) && item.persons.length,
                }]"
              >
                <v-list-item-avatar
                  :class="$vuetify.breakpoint.mdAndUp ? 'mr-4' : 'mr-2'"
                  :size="$vuetify.breakpoint.mdAndUp ? 40 : 30"
                >
                  <v-icon
                    :class="getIconColor(item.name)"
                    class="white--text"
                    :size="$vuetify.breakpoint.mdAndUp ? 24 : 20"
                  >
                    {{ getIcon(item.name) }}
                  </v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title v-text="item.name"/>
                  <v-list-item-subtitle>
                    {{ humanFileSize(item.size, false) }}
                    {{
                      !agent && fileProcess.personRole === 'signer' ? ' - allekirjoittamatta' : ''
                    }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle
                    v-if="agent || fileProcess.personRole === 'observer'"
                    class="mt-2 py-2"
                  >
                    <v-row
                      v-for="(person, indx) in item.persons"
                      :key="indx"
                      align="center"
                    >
                      <v-icon v-if="person.person_role === 'observer'" class="blue--text ml-3">
                        remove_red_eye
                      </v-icon>
                      <v-icon v-else-if="person.signature" class="green--text ml-3">
                        check
                      </v-icon>
                      <v-icon v-else class="grey--text text--lighten-2 ml-3">
                        close
                      </v-icon>
                      <p
                        class="my-0 ml-1"
                        :class="{'text-caption': $vuetify.breakpoint.smAndDown }"
                      >
                        {{ person.name }}
                        <span
                          v-if="person.signature_date && $vuetify.breakpoint.mdAndUp"
                          class="text-caption"
                        >
                          {{ formatDate(person.signature_date) }}
                        </span>
                      </p>
                    </v-row>
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-avatar>
                  <v-btn icon @click.stop="download(item.name, item.id)">
                    <v-icon>
                      save_alt
                    </v-icon>
                  </v-btn>
                </v-list-item-avatar>
                <v-list-item-avatar v-if="agent" class="ml-1">
                  <v-menu
                    top
                    offset-y
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        :disabled="!people.length"
                        icon
                        v-on="on"
                      >
                        <v-icon>
                          supervised_user_circle
                        </v-icon>
                      </v-btn>
                    </template>
                    <div class="px-4 pt-4 white text-caption" style="max-width: 290px;">
                      <p class="mb-0">
                        <i>
                          Valitse kenen allekirjoitus asiakirjalle tarvitaan, ja ketkä
                          katselijoista saavat nähdä ja ladata dokumentin.
                        </i>
                      </p>
                    </div>
                    <v-list
                      v-if="everythingLoaded"
                      dense
                    >
                      <v-list-item
                        v-for="(person, i) in people"
                        :key="`${i}_zsw`"
                        :disabled="item.persons.find(p => p.id === person.id)
                          && isset(item.persons.find(p => p.id === person.id).signature)"
                        @click.stop="filePerson(item, person)"
                      >
                        <v-list-item-action style="margin-right: 8px;">
                          <v-list-item-icon>
                            <v-icon
                              :disabled="item.persons
                                .find(p => p.id === person.id)
                                && isset(item.persons.find(p => p.id === person.id).signature)"
                              :color="item.persons
                                .some(p => p.id === person.id) ? 'primary' : 'grey'"
                            >
                              {{
                                item.persons.some(p => p.id === person.id)
                                  ? 'check_box'
                                  : 'check_box_outline_blank'
                              }}
                            </v-icon>
                          </v-list-item-icon>
                        </v-list-item-action>
                        <v-list-item-title>{{ person.name }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-list-item-avatar>
              </v-list-item>
              <v-divider :key="`${index}-`"/>
            </template>
          </v-list>
          <v-card
            v-if="agent"
            color="rgb(0,151,167,0.1)"
            text
            :class="showDropZone ? 'mx-6 dashed' : 'mx-5 mobile-drop-area-width'"
            class="pa-0 my-4 elevation-0"
          >
            <div id="dropArea" :class="!showDropZone ? 'mobile-drop-area v-btn elevation-2' : ''">
              <input
                v-if="uploadReady"
                id="file"
                ref="file"
                name="file"
                type="file"
                multiple
                @change="onChangeFileUpload()"
              >
              <v-row
                v-if="!uploading"
                align="center"
                justify="center"
                :class="!showDropZone ? '' : 'mt-4'"
              >
                <v-icon
                  :size="!showDropZone ? 24 : 38"
                  :class="!showDropZone ? 'white--text mb-1' : 'primary--text'"
                  class="mr-4"
                >
                  cloud_upload
                </v-icon>
                <span v-if="showDropZone">
                  Raahaa allekirjoitettavia asiakirjoja
                </span>
                <span v-else>
                  Lisää asiakirjoja
                </span>
              </v-row>
              <div v-else>
                Siirretään {{ files.length }} tiedosto{{ files.length > 1 ? 'a' : '' }}...
                <v-progress-linear
                  indeterminate
                  color="rgb(0,151,167)"
                />
              </div>
            </div>
          </v-card>
          <v-alert
            v-if="fileList && fileList.length > 0 && !agent && fileProcess.personRole === 'signer'"
            text
            color="primary"
            border="left"
            class="mx-2 mt-4"
          >
            <v-row
              align="center"
              no-gutters
            >
              <v-icon color="primary" class="mr-4">
                how_to_reg
              </v-icon>
              <v-col class="grow">
                {{ currentUser.name }}<br>
                {{ currentUser.ssn }}
              </v-col>
              <v-spacer/>
              <v-col class="shrink">
                <v-dialog
                  v-model="signDialog"
                  persistent
                  max-width="400"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      depressed
                      :disabled="!fileList.length"
                      color="primary"
                      v-on="on"
                    >
                      Allekirjoita
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-title class="text-h5">
                      Haluatko varmasti allekirjoittaa?
                    </v-card-title>
                    <v-card-text class="pt-3">
                      Olet allekirjoittamassa seuraavat asiakirjat:<br>
                      <p
                        v-for="(file, i) in fileList"
                        :key="i"
                        class="mb-1 mt-1 primary--text text-caption"
                      >
                        {{ i + 1 }}. {{ file.name }}
                      </p>
                      <v-checkbox
                        v-model="signAccept"
                        :label="`
                          Olen tutustunut allekirjoitettaviin asiakirjoihin huolella,
                          ja hyväksyn ne sellaisinaan.
                        `"
                        color="primary"
                        hide-details
                      />
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer/>
                      <v-btn text @click="signDialog = false">
                        Peruuta
                      </v-btn>
                      <v-btn
                        color="primary"
                        text
                        :disabled="!signAccept"
                        @click="signDocuments()"
                      >
                        Allekirjoita
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-col>
            </v-row>
          </v-alert>
        </v-card>

        <v-card
          v-if="agent"
          :class="{
            'ma-6 elevation-15 pb-2': $vuetify.breakpoint.mdAndUp,
            'elevation-0 mb-6 mt-5': $vuetify.breakpoint.smAndDown,
          }"
          :outlined="$vuetify.breakpoint.smAndDown"
        >
          <v-list
            two-line
            subheader
            dense
          >
            <v-subheader
              :class="$vuetify.breakpoint.mdAndUp ? 'px-6' : 'px-4'"
            >
              3. Lähetä allekirjoitettavaksi
            </v-subheader>
            <v-divider/>
          </v-list>
          <v-card-actions class="py-4">
            <v-dialog
              v-model="sendDialog"
              persistent
              max-width="330"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="primary"
                  :class="$vuetify.breakpoint.mdAndUp ? 'ml-6 mr-2' : 'ml-3 mr-2'"
                  large
                  :disabled="!people.length || !fileList.length"
                  v-on="on"
                >
                  Lähetä allekirjoitettavaksi
                </v-btn>
              </template>
              <v-card>
                <v-card-title class="text-h5">
                  Lähetä allekirjoitettavaksi
                </v-card-title>
                <v-card-text>
                  Olet lähettämässä linkin sähköiseen sopimusmenettelyyn seuraaville henkilöille:
                </v-card-text>
                <v-list
                  dense
                >
                  <v-list-item
                    v-for="(person, i) in people"
                    :key="`${i}_zsw`"
                    @click.stop="addToSendPersons(person)"
                  >
                    <v-list-item-action style="margin-right: 8px;" class="pa-0 ma-1">
                      <v-list-item-icon>
                        <v-icon
                          :color="sendPersons.some(p => p.id === person.id) ? 'primary' : 'grey'"
                        >
                          {{
                            sendPersons.some(p => p.id === person.id)
                              ? 'check_box'
                              : 'check_box_outline_blank'
                          }}
                        </v-icon>
                      </v-list-item-icon>
                    </v-list-item-action>
                    <v-list-item-title>{{ person.name }}</v-list-item-title>
                  </v-list-item>
                </v-list>
                <v-card-actions>
                  <v-spacer/>
                  <v-btn text @click="sendDialog = false">
                    Peruuta
                  </v-btn>
                  <v-btn
                    color="primary"
                    text
                    @click="sendLink()"
                  >
                    Lähetä
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-btn
              color="teal"
              dark
              class="mr-6"
              large
              :disabled="!people.length && !fileList.length"
              @click="print()"
            >
              <v-icon>print</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- TÄSÄ ALKAA -->
      </v-col>
      <v-col
        v-if="agent || fileProcess.personRole === 'observer'"
        cols="12"
        md="4"
      >
        <v-card
          color="transparent"
          class="elevation-0"
          :class="$vuetify.breakpoint.smAndDown ? 'mt-0 px-0' : 'mt-10 px-4'"
        >
          <h2 class="text-h5 mb-6">
            Tapahtumat
          </h2>
          <v-timeline dense>
            <v-timeline-item
              v-for="(item, i) in logItems"
              :key="i"
              color="primary"
              small
            >
              <div
                class="elevation-0"
              >
                <p class="my-2 text-caption">
                  <b class="primary--text">
                    {{ formatDate(item.logTimeStamp) }}
                  </b>
                  <br>
                  {{ item.name }} {{ item.description }}
                </p>
              </div>
            </v-timeline-item>
          </v-timeline>
        </v-card>
      </v-col>
    </v-row>
    <v-snackbar
      v-model="messageBox"
      :color="messageColor"
    >
      {{ message }}
    </v-snackbar>
  </v-container>
  <div
    v-else
    class="text-center mt-12 pt-10"
  >
    <v-progress-circular
      :size="50"
      color="primary"
      indeterminate
    />
  </div>
</template>
<script>
/* eslint-disable no-await-in-loop */
import axios from 'axios';
import { mapState, mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import _ from 'lodash';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib'; // eslint-disable-line
import download from 'downloadjs'; // eslint-disable-line
import { apiGet, apiPost } from '../api';

const pdfMake = require('pdfmake/build/pdfmake.js');
const pdfFonts = require('../assets/fonts.js');

export default {
  name: 'SignFiles',
  components: {
    gdpr: () => import('@/components/Gdpr'),
    'file-signer-form': () => import('@/components/FileSignerForm'),
  },
  data: () => ({
    pageSize: 6,
    signatures: [],
    addingPerson: false,
    sessionLoaded: false,
    messageBox: false,
    message: '',
    messageColor: 'error',
    loadingFileList: false,
    axios,
    loadingPerson: null,
    acceptGdpr: false,
    showGdpr: false,
    _timerId: null,
    dialog: false,
    signDialog: false,
    sendDialog: false,
    signer: false,
    uploadReady: true,
    files: [],
    sendPersons: [],
    uploading: false,
    fileList: [],
    selectedPersonId: '',
    everythingLoaded: false,
    showSearch: false,
    search: null,
    select: null,
    signedFiles: [],
    logItems: [],
    fileProcess: {},
    signAccept: false,
    companyId: null,
    newPerson: {
      name: '',
      email: '',
      ssn: '',
      role: 'signer',
      id: null,
    },
    people: [],
    customers: [],
    loading: false,
  }),

  computed: {
    ...mapGetters({
      userDetails: 'userDetails',
    }),
    ...mapState({
      agent: state => state.agent,
      identification: state => state.identification,
      currentUser: state => state.sessionUser,
      currentProperty: state => state.currentProperty,
    }),
    usersThatHaveSigned() {
      let filePersons = [];
      this.fileList.forEach((file) => {
        (filePersons = [...filePersons, ...file.persons]);
      });
      return filePersons.filter(p => p.signature).map(p => p.id);
    },
    showDropZone() {
      // Internet Explorer 6-11
      const isIE = /* @cc_on!@ */false || !!document.documentMode;

      // Edge 20+
      const isEdge = !isIE && !!window.StyleMedia;

      const isMobile = this.$vuetify.breakpoint.smAndDown;

      if (isIE) return false;
      if (isEdge) return false;
      if (isMobile) return false;
      return true;
    },
  },
  watch: {
    search(val) {
      if (val && val !== this.select) {
        this.querySelections(val);
      }
    },
    selectedPersonId: {
      async handler(val) { // eslint-disable-line
        if (this.selectedPersonId !== '') {
          this.newPerson = {
            ..._.cloneDeep(this.customers.find(c => c.id === this.selectedPersonId)),
            role: 'signer',
          };
        }
      },
      deep: true,
    },
  },

  async mounted() {
    this.loadingFileList = true;
    await this.getIdentification();
    await this.getFileProcessPeople();
    this.sessionLoaded = true;
    this.everythingLoaded = true;
    if (this.$route.params.id) {
      await this.getFileList();
    }
  },
  methods: {
    ...mapActions({
      getIdentification: 'getIdentification',
      setCurrentProperty: 'setCurrentProperty',
    }),

    addSignatureDetails({ pdf, font }) {
      const pages = this.createPages(pdf);
      let currentPage = 0;

      const signatureChunks = this.splitIntoChunks(this.signatures.filter(s => s.signature_date));

      for (const page of pages) {
        let yPosition = page.getHeight() - 32;
        const gap = 20;
        const lineHeight = 13;

        if (currentPage === 0) {
          const headings = ['Signatures', 'Allekirjoitukset'];

          page.drawText(headings.join(' | '), {
            font,
            size: 14,
            x: 16,
            y: yPosition,
          });

          // add some extra margin after the heading
          yPosition -= lineHeight;
        }

        for (const signature of signatureChunks[currentPage]) {
          yPosition -= gap;

          const lines = [
            {
              labels: ['Name', 'Nimi'],
              value: signature.signer_name || signature.name,
            },
            // {
            //   labels: ['Email', 'Sähköposti'],
            //   value: signature.email,
            // },
            {
              labels: ['Identification provider', 'Tunnistautumisen palveluntarjoaja'],
              value: 'Nets Trust Services (Finnish Trust Network / Traficom)',
            },
            {
              labels: ['Time of the signature', 'Allekirjoitusaika'],
              value: signature.signature_date,
            },
            {
              labels: ['Signature identifier', 'Allekirjoituksen tunniste'],
              value: ' ',
            },
          ];

          const sigChunks = this.chunk(signature.signature, 110);

          for (const line of lines) {
            const content = `${line.labels.join(' / ')}: ${line.value || '-'}`;

            page.drawText(content, {
              font,
              size: 9,
              x: 16,
              y: yPosition,
            });

            yPosition -= lineHeight;
          }

          for (const sigLine of sigChunks) {
            page.drawText(sigLine, {
              font,
              size: 8,
              x: 16,
              y: yPosition,
            });
            yPosition -= 12;
          }
        }

        currentPage += 1;
      }
    },

    createPages(pdf) {
      const signedPersons = this.signatures.filter(signature => signature.signature_date);
      const pageCount = Math.ceil(signedPersons.length / this.pageSize);
      const pages = [];

      for (let i = 0; i < pageCount; i += 1) {
        pages.push(pdf.addPage());
      }

      return pages;
    },

    print() {
      pdfMake.vfs = pdfFonts.pdfMake.vfs;
      pdfMake.fonts = {
        Roboto: {
          normal: 'Roboto-Regular.ttf',
          bold: 'Roboto-Medium.ttf',
          italics: 'Roboto-Italic.ttf',
          bolditalics: 'Roboto-MediumItalic.ttf',
        },
        Icons: {
          normal: 'Icons.ttf',
        },
      };

      const content = {
        table: {
          widths: [360, 120],
          body: [
            [
              {
                text: 'SÄHKÖINEN SOPIMUSMENETTELY',
                fontSize: 14,
                color: '#222222',
                colSpan: 2,
                bold: true,
                margin: [0, 0, 0, 0],
              },
              ' ',
            ],
            [
              {
                text: `Luontiajankohta: ${this.formatDate(this.fileProcess.create_date)}`,
                fontSize: 9,
                color: '#333333',
                colSpan: 2,
                margin: [0, 0, 0, 0],
              },
              ' ',
            ],
            [
              {
                text: `Kohde #${this.fileProcess.property_number}   |   ${this.fileProcess.property_name}   |   ${this.fileProcess.name}`,
                fontSize: 9,
                color: '#333333',
                colSpan: 2,
                margin: [0, 0, 0, 30],
              },
              ' ',
            ],
            [
              {
                text: 'Välitysliike:',
                bold: true,
              },
              {
                text: 'Tunnistautuminen:',
                bold: true,
              },
            ],
            [
              {
                text: this.currentProperty.companyName,
              },
              {
                text: 'Nets Branch Norway',
              },
            ],
            [
              {
                text: this.currentProperty.companyVat,
              },
              {
                text: 'NO 996345734 MVA',
              },
            ],
            [' ', ' '],
          ],
        },
        margin: [0, -10, 0, 7],
        layout: 'noBorders',
      };

      this.fileList.forEach((file) => {
        content.table.body.push([' ', ' ']);
        content.table.body.push([{
          text: file.name,
          bold: true,
        }, {
          text: this.humanFileSize(file.size, false),
        }]);
        file.persons.forEach((person) => {
          if (person.person_role === 'signer') {
            let signature = 'Ei allekirjoitusta';
            if (person.signature) {
              signature = this.chunk(`Tunniste: ${person.signature}`, 20).join(' ');
              signature = `${signature}\n\nAllekirjoitettu: ${this.formatDate(person.signature_date)}`;
            }
            content.table.body.push([{
              table: {
                widths: [355, 120],
                body: [
                  [{ text: person.name }, { text: person.ssn }],
                  [{ text: signature, colSpan: 2, fontSize: 8 }, ''],
                ],
              },
              layout: {
                hLineColor: () => '#333333',
                vLineColor: () => '#333333',
                hLineWidth: () => 0.2,
                vLineWidth: () => 0.2,
              },
              colSpan: 2,
              margin: [0, 0, 0, 5],
            }, '']);
          }
        });
      });

      const docDefinition = {
        footer: (currentPage, pageCount) => ({
          color: '#666666',
          text: `Sähköinen sopimusmenettely   -   Kohde #${this.fileProcess.property_number}   |   ${this.fileProcess.property_name}   |   ${this.fileProcess.name}     Sivu: ${currentPage.toString()} / ${pageCount}`,
          fontSize: 7,
          alignment: 'right',
          width: '50%',
          margin: [0, 0, 50, 0],
        }),
        content,
        defaultStyle: {
          font: 'Roboto',
          fontSize: 9,
        },
        pageMargins: [50, 50, 50, 30],
        styles: {
          grayLines: {
            hLineColor: () => 'gray',
            vLineColor: () => 'gray',
          },
        },
      };
      pdfMake.createPdf(docDefinition).download('Todistus.pdf');
    },

    chunk(str, n) {
      const ret = [];
      let i;
      let len;

      for (i = 0, len = str.length; i < len; i += n) {
        ret.push(str.substr(i, n));
      }

      return ret;
    },

    async getFileProcessPeople() {
      const response = await apiGet(`customer/fileprocesspeople/${this.$route.params.id}`);
      this.people = response.result;
      this.sendPersons = this.people;
    },
    fetchEntriesDebounced(payload) {
      // cancel pending call
      clearTimeout(this._timerId);
      const that = this;
      // delay new call 500ms
      this._timerId = setTimeout(async () => {
        const response = await apiPost('customer/search', { query: payload });
        that.customers = response.result;
        that.loading = false;
      }, 500);
    },
    isset(value) {
      if (value !== '' && value !== undefined) return true;
      return false;
    },
    async querySelections(payload) {
      if (payload === '') return;
      if (payload.length < 3) return;
      this.loading = true;
      this.fetchEntriesDebounced(payload);
    },
    async addPersonToSigningProcess() {
      this.addingPerson = true;
      this.$bus.$emit('SHOW_GLOBAL_LOADER');
      const params = {
        processId: this.$route.params.id,
        personId: this.newPerson.id,
        name: this.newPerson.name,
        ssn: this.newPerson.ssn,
        email: this.newPerson.email,
        role: this.newPerson.role,
      };
      const response = await apiPost('customer/addsigner', params);
      this.dialog = false;
      let succ = false;
      if (response.success === 'false' && response.message === 'person exists in the process') {
        this.messageColor = 'error';
        this.message = 'Olet jo lisännyt tämän henkilön!';
        succ = true;
      } else if (response.success === 'true') {
        this.messageColor = 'success';
        this.message = 'Henkilö lisätty onnistuneesti';
        succ = true;
        await this.log(`lisäsi henkilön ${this.newPerson.name} sopimusmenettelyyn`, this.userDetails.nimi);
        // add person to all files
        const personParams = {
          ...this.newPerson,
          ...(response.person_id ? { id: response.person_id } : {}),
        };

        for (const file of this.fileList) {
          await this.addPersonToFile(file, personParams);
        }
      }
      await this.getFileList();
      await this.getFileProcessPeople();
      this.sendPersons = this.people;
      this.$bus.$emit('HIDE_GLOBAL_LOADER');
      this.messageBox = succ;
      this.addingPerson = false;
    },
    async sendLink() {
      const params = {
        processId: this.$route.params.id,
        persons: this.sendPersons,
      };
      await apiPost('dropbox/sendlink', params);

      console.warn(params);
      this.sendDialog = false;
      this.messageColor = 'success';
      this.message = 'Linkki lähetetty!';
      this.messageBox = true;
      let personMessage = '';
      let x = 0;
      this.sendPersons.forEach((p) => {
        if (x === 0) {
          (personMessage = p.name);
        } else {
          (personMessage = `${personMessage}, ${p.name}`);
        }
        x += 1;
      });
      await this.log(`lähetti linkin henkilöille: ${personMessage}.`, this.userDetails.nimi);
    },
    async log(message, name = null) {
      const params = {
        description: message,
      };
      if (name) {
        params.name = name;
      }
      await apiPost('dropbox/log', params);
      const logresponse = await apiGet(`dropbox/log/${this.$route.params.id}`);
      this.logItems = logresponse.result;
    },
    async removePersonFromSigningProcess(person) {
      const params = {
        processId: this.$route.params.id,
        personId: person.id,
      };
      const response = await apiPost('customer/removepersonfromsigning', params);
      await this.getFileList();
      if (response.success === 'false') {
        this.messageColor = 'error';
        this.message = 'Virhe';
        this.messageBox = true;
      } else if (response.success === 'true') {
        this.messageColor = 'success';
        this.message = 'Henkilö poistettu onnistuneesti';
        this.messageBox = true;
        await this.log(`poisti henkilön ${person.name} sopimusmenettelystä`, this.userDetails.nimi);
      }
      await this.getFileProcessPeople();
    },
    resetForm() {
      this.newPerson.name = '';
      this.newPerson.email = '';
      this.newPerson.ssn = '';
      this.newPerson.id = null;
      this.selectedPersonId = '';
    },
    // customFilter (item, queryText, itemText) {
    //   const textOne = item.name.toLowerCase()
    //   const textTwo = item.ssn.toLowerCase()
    //   const searchText = queryText.toLowerCase()

    //   return textOne.indexOf(searchText) > -1 ||
    //     textTwo.indexOf(searchText) > -1
    // },
    clear() {
      this.uploadReady = false;
      this.$nextTick(() => {
        this.uploadReady = true;
      });
    },
    submitForm() {
      const formData = new FormData();
      for (let i = 0; i < this.files.length; i += 1) {
        formData.append('example3[]', this.files[i]);
        console.warn(this.files[i]);
      }
      // formData.append('example3', this.files);

      this.axios.post(process.env.NODE_ENV === 'production'
        ? `/public/backend/api/v1/dropbox/upload/${this.$route.params.id}`
        : `http://localhost:8888/public/backend/api/v1/dropbox/upload/${this.$route.params.id}`,
      formData,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).then(async (data) => {
        console.log(data.data);
        this.clear();
        this.uploading = false;
        await this.getFileList();
      })
        .catch(() => {
          this.uploading = false;
          this.clear();
        });
    },

    onChangeFileUpload() {
      this.uploading = true;
      this.files = this.$refs.file.files;
      this.submitForm();
    },

    async getFileList() {
      this.loadingFileList = true;
      const response = await apiGet(`dropbox/list/${this.$route.params.id}`);
      this.fileList = [];
      const process = await apiGet(`customer/fileprocess/${this.$route.params.id}`);
      if (process.success === 'true') {
        this.companyId = process.result.company_id;
        this.fileProcess = process.result;
        if (this.agent) {
          await this.setCurrentProperty(process.result.property_id);
        }
      }
      const logresponse = await apiGet(`dropbox/log/${this.$route.params.id}`);
      this.logItems = logresponse.result;

      for (let s = 0; s < response.result.length; s += 1) {
        const filePersons = await apiGet(`customer/filepersons/${response.result[s].id}`);
        const newFile = response.result[s];
        newFile.persons = filePersons.result;
        this.fileList.push(newFile);
      }
      if (this.fileProcess.personRole === 'signer') {
        const sfResponse = await apiGet(`dropbox/signedfiles/${this.$route.params.id}`);
        this.signedFiles = [];
        for (let s = 0; s < sfResponse.result.length; s += 1) {
          const filePersons = await apiGet(`customer/filepersonssigner/${sfResponse.result[s].id}`);
          const newFile = sfResponse.result[s];
          newFile.persons = filePersons.result;
          this.signedFiles.push(newFile);
        }
        // this.signedFiles = sfResponse.result;
      }
      this.loadingFileList = false;
    },
    async download(name, id) {
      const options = {
        baseURL: process.env.NODE_ENV === 'production'
          ? '/public/backend/api/v1/dropbox/download/'
          : 'http://localhost:8888/public/backend/api/v1/dropbox/download/',
        withCredentials: true,
        responseType: 'blob',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      };
      const instance = axios.create(options);
      const response = await instance.get(String(id));

      const url = window.URL.createObjectURL(new Blob([response.data]));

      if (name.endsWith('pdf')) {
        let file = this.fileList.find(f => f.id === id);
        if (!file) {
          file = this.signedFiles.find(f => f.id === id);
        }
        this.signatures = file.persons;

        const pdf = await this.preparePdf(url);
        download(pdf, name, 'application/pdf');
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', name);
        document.body.appendChild(link);
        link.click();
      }
    },

    async preparePdf(url) {
      const existingPdfBytes = await fetch(url).then(res => res.arrayBuffer());

      const pdf = await PDFDocument.load(existingPdfBytes);
      const font = await pdf.embedFont(StandardFonts.Helvetica);
      // do not modify the pdf if there are no signatures
      if (this.signatures.length && this.signatures.some(s => s.signature_date)) {
        this.addSignatureDetails({ pdf, font });

        const pages = pdf.getPages();

        const pageFooter = (page, total) => {
          const footer = `Helmi-kiinteistönvälitysjärjestelmä    |    Identification / Tunnistautuminen : Nets Trust Services (FTN)    |    Page (${page} of ${total})`;
          // const signed = this.signatures.filter(signature => signature.signature_date);

          // footer += `    ${signed.length} out of ${this.signatures.length} parties `;
          // footer += `${signed.length > 1 ? 'has' : 'have'} signed this document`;

          return footer;
        };

        let pageNumber = 1;
        const total = pages.length;

        for (const page of pages) {
          // const { height } = page.getSize();
          const esignatures = [
            'Electronically signed',
            'Sähköisesti allekirjoitettu',
            'Elektoniskt signerats',
            'Elektonisk signert',
            'Elektonisk underskrevet',
          ];

          page.drawText(esignatures.join(' | '), {
            font,
            size: 6,
            color: rgb(0.4, 0.4, 0.4),
            x: 16,
            y: 24,
          });

          page.drawText(pageFooter(pageNumber, total), {
            font,
            size: 6,
            color: rgb(0.4, 0.4, 0.4),
            x: 16,
            y: 14,
          });

          pageNumber += 1;
        }
      }

      const pdfBytes = await pdf.save();

      return pdfBytes;
    },

    splitIntoChunks(array, size = this.pageSize) {
      const chunks = [];
      let index = 0;

      while (index < array.length) {
        chunks.push(array.slice(index, size + index));
        index += size;
      }
      return chunks;
    },

    getIcon(name) {
      if (name.endsWith('png') || name.endsWith('jpg') || name.endsWith('jpg') || name.endsWith('psd') || name.endsWith('jpeg')) {
        return 'insert_photo';
      } if (name.endsWith('pdf')) {
        return 'picture_as_pdf';
      }
      return 'assignment';
    },
    getIconColor(name) {
      if (name.endsWith('png') || name.endsWith('jpg') || name.endsWith('jpg') || name.endsWith('psd') || name.endsWith('jpeg')) {
        return 'orange';
      } if (name.endsWith('pdf')) {
        return 'red';
      }
      return 'blue';
    },
    formatDate(date) {
      if (!date) return ' ';
      return moment(date).format('DD.MM.YYYY HH:mm');
    },
    humanFileSize(bytes, si) {
      const thresh = si ? 1000 : 1024;
      if (Math.abs(bytes) < thresh) {
        return `${bytes} B`;
      }
      const units = si
        ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        : ['Kt', 'Mt', 'Gt', 'Tt', 'PiB', 'EiB', 'ZiB', 'YiB'];
      let u = -1;
      let bytes2 = bytes;
      do {
        bytes2 /= thresh;
        u += 1;
      } while (Math.abs(bytes2) >= thresh && u < units.length - 1);
      return `${bytes2.toFixed(1)} ${units[u]}`;
    },
    async netsLink() {
      const state = `doc_${this.$route.params.id}`;

      const response = await apiPost('nets/get-link', { state });

      window.location.href = response.result;
    },
    validateEmail(email) {
      const validEmail = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return email.match(validEmail);
    },
    validateSsn(ssn) {
      const validssn = /^(0[1-9]|[12]\d|3[01])(0[1-9]|1[0-2])([5-9]\d\+|\d\d-|[01]\dA)\d{3}[\dABCDEFHJKLMNPRSTUVWXY]$/;
      return ssn.match(validssn);
    },
    async addPersonToFile(file, person) {
      const params = {
        fileId: file.id,
        personId: person.id,
      };
      await apiPost('customer/addtofile', params);
    },
    async signFile(file) {
      const params = {
        fileId: file.id,
      };
      await apiPost('customer/signfile', params);
    },
    async asyncForEach(array, callback) {
      // eslint-disable-next-line no-plusplus
      for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array);
      }
    },
    async signDocuments() {
      this.signDialog = false;
      const fileCount = this.fileList.length;
      await this.asyncForEach(this.fileList, this.signFile);
      await this.log(`allekirjoitti ${fileCount} tiedostoa`);
      // await this.fileList.forEach(async file => {
      //   await this.signFile(file);
      // });
      await this.getFileList();
      this.messageColor = 'success';
      this.message = `${fileCount} tiedostoa allekirjoitettu`;
      this.messageBox = true;
    },
    async removePersonFromFile(file, person) {
      const params = {
        fileId: file.id,
        personId: person.id,
      };
      await apiPost('customer/removefromfile', params);
    },
    async filePerson(file, person) {
      this.loadingPerson = person.id;
      if (file.persons.some(p => p.id === person.id)) {
        await this.removePersonFromFile(file, person);
        // eslint-disable-next-line no-param-reassign
        file.persons = file.persons.filter(p => p.id !== person.id);
      } else {
        file.persons.push(person);
        await this.addPersonToFile(file, person);
      }
      this.loadingPerson = null;
    },
    addToSendPersons(person) {
      if (this.sendPersons.some(p => p.id === person.id)) {
        this.sendPersons = this.sendPersons.filter(p => p.id !== person.id);
      } else {
        this.sendPersons.push(person);
      }
    },
  },
};
</script>
<style lang="scss">
  #dropArea {
    position: relative;
    width: 100%;
    height: 120px;
    font-size: 14px;
    font-weight: 500;
    text-transform: uppercase;
    text-align: center;
    padding-top: 24px;
    color: rgb(0,151,167);
    background-color: rgba(0,151,167,0);
    transition: background-color 0.5s ease;
  }
  #dropArea:HOVER {
    background-color: rgba(0,151,167,0.1);
  }

  #dropArea.mobile-drop-area {
    padding-top: 0;
    height: 44px;
    color: #fff;
    font-weight: 500;
    font-size: 14px;
    text-transform: uppercase;
    background-color:rgb(0, 151, 167);
  }

  .mobile-drop-area-width {
    max-width: 220px;
  }

  #file {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
    right: 0;
    left: 0;
    opacity: 0;
  }
  .dashed {
    border: 2px dashed rgb(0, 101, 112);
  }
</style>
